import React, { useEffect, useState, useRef } from 'react';
import { socket } from '../admin/chat/socket';

const AUDIO_MIME_BY_EXT = {
  '.ogg':  'audio/ogg',
  '.opus': 'audio/ogg; codecs=opus',
  '.mp3':  'audio/mpeg',
  '.m4a':  'audio/mp4',
  '.aac':  'audio/aac',
  '.wav':  'audio/wav',
  '.amr':  'audio/amr',
};
const IMAGE_EXTS = ['.jpg', '.jpeg', '.png', '.gif', '.webp'];

function getExt(url = '') {
  try {
    const u = new URL(url);
    const path = (u.pathname || '').toLowerCase();
    const dot = path.lastIndexOf('.');
    return dot >= 0 ? path.slice(dot) : '';
  } catch {
    // fallback if URL constructor fails
    const path = url.split('?')[0].toLowerCase();
    const dot = path.lastIndexOf('.');
    return dot >= 0 ? path.slice(dot) : '';
  }
}

function isAudioUrl(url = '') {
  const ext = getExt(url);
  return Object.prototype.hasOwnProperty.call(AUDIO_MIME_BY_EXT, ext);
}
function isImageUrl(url = '') {
  const ext = getExt(url);
  return IMAGE_EXTS.includes(ext);
}

function resolveMediaType(m) {
  // Prefer server’s classification if provided
  if (m.media_type) return m.media_type;

  if (m.latitude != null && m.longitude != null) return 'location';
  if (m.media_url) {
    if (isAudioUrl(m.media_url)) return 'audio';
    if (isImageUrl(m.media_url)) return 'image';
  }
  return null;
}

function guessAudioMime(url='') {
  const ext = getExt(url);
  return AUDIO_MIME_BY_EXT[ext] || 'audio/ogg';
}

export function ChatViewDriver({ chatId, onBackClick, userId }) {
  const [messages, setMessages] = useState([]);
  const [draft, setDraft]     = useState('');
  const [sessionOpen, setSessionOpen] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [file, setFile] = useState(null);
  const [previewURL, setPreviewURL] = useState('');
  const [caption, setCaption] = useState('');
  const [uploading, setUploading] = useState(false);
  const [showAudioModal, setShowAudioModal] = useState(false);
  const [recording, setRecording] = useState(false);
  const [audioBlob, setAudioBlob] = useState(null);
  const [audioURL, setAudioURL] = useState('');
  const mediaRecorderRef = useRef(null);
  const [phoneNumber, setPhoneNumber] = useState('');
  const endRef               = useRef(null);

  // scroll helper
  const scrollToBottom = () => {
    endRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(scrollToBottom, [messages]);

  useEffect(() => {
    if (chatId == null) return;

    // --- normalize once ---
    const currentId = Number(chatId);
    const room = `chat_${currentId}`;
    console.log("[chatview] mount -> chatId:", chatId, "normalized:", currentId, "room:", room);

    // ---- initial data fetches ----
    fetch(`${process.env.REACT_APP_API_URL}/api/admin/chats/${currentId}/messages`)
      .then(r => r.json())
      .then(data => {
        console.log("[fetched_messages]", data);
        setMessages(data);
        if (Array.isArray(data) && data.length) {
          const candidate =
            data[0].phone_number ??
            data[0].from ??
            data[0].sender_phone ??
            null;
          if (candidate) setPhoneNumber(candidate);
        }
      })
      .catch(err => console.log("[fetch messages] error:", err));

    fetch(`${process.env.REACT_APP_API_URL}/api/admin/chats/${currentId}/session`)
      .then(r => r.json())
      .then(data => setSessionOpen(!!data.session_open))
      .catch(err => console.log("[fetch session] error:", err));

    // ---- join room (only after connected) ----
    const doJoin = () => {
      console.log("[socket] joining room:", room, "connected:", socket.connected);
      socket.emit("admin_join_chat", room, (ack) => {
        console.log("[socket] join ack =>", ack);
      });
    };

    let onConnect;
    if (socket.connected) {
      doJoin();
    } else {
      onConnect = () => {
        console.log("[socket] connected -> will join", room);
        doJoin();
      };
      socket.once("connect", onConnect);
    }

    // ---- message handler (type-safe compare) ----
    const onNewMessage = (msg) => {
      const incomingId = Number(msg?.chat_id);
      console.log("[socket] new_message incoming:", msg, "compare:", incomingId, "vs", currentId);
      if (incomingId !== currentId) return;

      setMessages(prev => [...prev, msg]);

      // Set phone number if not yet set
      setPhoneNumber(prev =>
        prev ||
        msg.phone_number ||
        msg.from ||
        msg.sender_phone ||
        null
      );
    };

    // extra diagnostics
    const onDisconnect = (reason) => console.log("[socket] disconnect:", reason);
    const onConnectError = (err) => console.log("[socket] connect_error:", err?.message || err);
    const onError = (err) => console.log("[socket] error:", err);

    socket.on("new_message", onNewMessage);
    socket.on("disconnect", onDisconnect);
    socket.on("connect_error", onConnectError);
    socket.on("error", onError);

    // ---- cleanup ----
    return () => {
      console.log("[chatview] unmount -> leaving", room);
      socket.off("new_message", onNewMessage);
      socket.off("disconnect", onDisconnect);
      socket.off("connect_error", onConnectError);
      socket.off("error", onError);
      if (onConnect) socket.off("connect", onConnect);

      socket.emit("admin_leave_chat", room, (ack) => {
        console.log("[socket] leave ack =>", ack);
      });
    };
  }, [chatId]);

  const send = () => {
    if (!draft.trim()) return;
    fetch(`${process.env.REACT_APP_API_URL}/api/admin/chats/${chatId}/messages`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ content: draft })
    });
    setDraft('');
  };

  const sendTemplate = () => {
    // call your template‐send helper route:
    fetch(`${process.env.REACT_APP_API_URL}/api/admin/chats/${chatId}/send_de_camino`, { method: "POST" })
      .then((r) => r.json())
      .then(console.log)
      .catch(console.error);
  };

  //Image related functions:
  const openModal = () => {
    if (!sessionOpen) return; // safeguard: only allow free text/media inside 24h
    setShowModal(true);
  };
  const closeModal = () => {
    setShowModal(false);
    setFile(null);
    setPreviewURL('');
    setCaption('');
  };
  const pickFile = e => {
    const f = e.target.files?.[0];
    if (!f) return;
    setFile(f);
    setPreviewURL(URL.createObjectURL(f));
  };
  const onDrop = e => {
    e.preventDefault();
    const f = e.dataTransfer.files?.[0];
    if (!f) return;
    setFile(f);
    setPreviewURL(URL.createObjectURL(f));
  };
  const onDragOver = e => e.preventDefault();

  const sendImage = async () => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('image', file, file.name);
      if (caption?.trim()) fd.append('caption', caption.trim());

      const resp = await fetch(`${process.env.REACT_APP_API_URL}/api/admin/chats/${chatId}/media`, {
        method: 'POST',
        body: fd
      });
      if (!resp.ok) {
        const err = await resp.text();
        console.error('upload/send failed:', err);
      }
      // On success, server emits 'new_message' so ChatView will update automatically
      closeModal();
    } catch (err) {
      console.error(err);
    } finally {
      setUploading(false);
    }
  };

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mr = new MediaRecorder(stream);
      mediaRecorderRef.current = mr;
      const chunks = [];
      mr.ondataavailable = (e) => { if (e.data.size > 0) chunks.push(e.data); };
      mr.onstop = () => {
        const blob = new Blob(chunks, { type: 'audio/ogg; codecs=opus' });
        setAudioBlob(blob);
        setAudioURL(URL.createObjectURL(blob));
        stream.getTracks().forEach(t => t.stop());
        setRecording(false);
      };
      mr.start();
      setRecording(true);
    } catch (e) {
      console.error('mic access error', e);
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && recording) {
      mediaRecorderRef.current.stop();
    }
  };



  return (
  <div style={{
    display: 'flex',
    flexDirection: 'column',
    height: '100dvh',
    minHeight: 0,
    overflow: 'hidden',
    background: '#0b141a',
    color: '#e9edef',
    paddingTop: 'env(safe-area-inset-top)',
    paddingBottom: 'env(safe-area-inset-bottom)'
  }}>
    {/* Header */}
    <div style={{
      height: 'clamp(48px, 6.5vh, 56px)',
      display: 'flex',
      alignItems: 'center',
      gap: 'clamp(8px, 2vw, 12px)',
      padding: '0 clamp(8px, 3vw, 12px)',
      background: '#202c33',
      borderBottom: '1px solid #222e35',
      flexShrink: 0,
      position: 'sticky',
      top: 0,
      zIndex: 2
    }}>
      <button
        onClick={onBackClick}
        title="Back"
        style={{
          background: '#2a3942',
          color: '#e9edef',
          border: '1px solid #3b4a52',
          borderRadius: 8,
          padding: 'clamp(6px, 1.6vh, 10px) clamp(8px, 2vw, 12px)',
          cursor: 'pointer',
          fontWeight: 600,
          touchAction: 'manipulation',
          minHeight: 36,
          minWidth: 44
        }}
      >
        ← Back
      </button>
      <div style={{ fontWeight: 700, color: '#e9edef', fontSize: 'clamp(14px, 2.8vw, 16px)' }}>{phoneNumber}</div>
    </div>

    {/* Messages scroll area */}
    <div style={{
      flex: '1 1 auto',
      minHeight: 0,
      overflowY: 'auto',
      padding: 'clamp(8px, 2vw, 16px) clamp(8px, 3vw, 12px)',
      background: 'url("https://storage.googleapis.com/fastmax-images/51046e67-3895-44b2-a2a6-bd1020f4b1f3-OIP.jpeg") repeat',
      backgroundColor: '#0b141a',
      backgroundSize: 'auto 256px',
      backgroundPosition: 'top left',
      WebkitOverflowScrolling: 'touch',
      overscrollBehavior: 'contain'
    }}>
      {messages.map(m => {
        const kind = resolveMediaType(m);
        const isOutbound = m.direction === 'outbound';

        const bubbleBg   = isOutbound ? '#005c4b' : '#202c33';
        const bubbleClr  = '#e9edef';
        const alignStyle = { display: 'flex', justifyContent: isOutbound ? 'flex-end' : 'flex-start' };

        return (
          <div key={m.id} style={{ ...alignStyle, margin: '6px 0' }}>
            <div style={{
              maxWidth: 'min(78%, 92vw)',
              background: bubbleBg,
              color: bubbleClr,
              padding: 'clamp(8px, 2.2vw, 12px)',
              borderRadius: 12,
              borderTopLeftRadius: isOutbound ? 12 : 4,
              borderTopRightRadius: isOutbound ? 4 : 12,
              boxShadow: '0 1px 1px rgba(0,0,0,0.25)',
              overflow: 'hidden',
              wordBreak: 'break-word',
              overflowWrap: 'anywhere',
              lineHeight: 1.35
            }}>
              {/* CONTENT */}
              {kind === 'location' ? (
                <div>
                  <div style={{ fontWeight: 700, marginBottom: 4, fontSize: 'clamp(13px, 2.6vw, 15px)' }}>📍 Location</div>
                  <a
                    href={`https://www.google.com/maps?q=${m.latitude},${m.longitude}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ color: '#9de1fe', textDecoration: 'none', display: 'inline-block', maxWidth: '100%' }}
                  >
                    {m.content || `${m.latitude}, ${m.longitude}`}
                  </a>
                </div>
              ) : kind === 'audio' ? (
                <div>
                  <div style={{ marginBottom: 6, fontWeight: 600, fontSize: 'clamp(13px, 2.6vw, 15px)' }}>🎤 Voice message</div>
                  <audio
                    controls
                    preload="metadata"
                    style={{ width: '100%' }}
                  >
                    <source src={m.media_url} type={guessAudioMime(m.media_url)} />
                    Your browser can’t play this audio.
                  </audio>
                  <div style={{ marginTop: 6 }}>
                    <a
                      href={m.media_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{ color: '#9de1fe', textDecoration: 'none', fontSize: 'clamp(11px, 2.4vw, 12px)' }}
                    >
                      Download audio
                    </a>
                  </div>
                  {m.content && (
                    <div style={{ fontSize: 'clamp(12px, 2.6vw, 13px)', opacity: 0.9, marginTop: 6 }}>{m.content}</div>
                  )}
                </div>
              ) : kind === 'image' ? (
                <div>
                  <div style={{
                    borderRadius: 10,
                    overflow: 'hidden',
                    background: '#111b21',
                    marginBottom: m.content ? 6 : 0,
                    maxHeight: '60dvh'
                  }}>
                    <img
                      src={m.media_url}
                      alt=""
                      style={{ width: '100%', height: 'auto', display: 'block' }}
                      onError={() => console.warn('[ChatView] image failed to load:', m.media_url)}
                    />
                  </div>
                  {m.content && (
                    <div style={{ fontSize: 'clamp(12px, 2.6vw, 13px)', opacity: 0.95 }}>{m.content}</div>
                  )}
                </div>
              ) : (
                <div style={{ fontSize: 'clamp(14px, 3.2vw, 15px)', lineHeight: 1.4, whiteSpace: 'pre-wrap' }}>
                  {m.content}
                </div>
              )}

              {/* timestamp */}
              <div style={{
                display: 'flex',
                justifyContent: 'flex-end',
                marginTop: 4,
                fontSize: 'clamp(10px, 2.4vw, 11px)',
                color: 'rgba(233,237,239,0.7)'
              }}>
                {new Date(m.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
              </div>
            </div>
          </div>
        );
      })}
      <div ref={endRef} />
    </div>

    {/* Composer */}
    <div style={{
      padding: 'clamp(8px, 2vw, 12px)',
      paddingBottom: 'max(env(safe-area-inset-bottom), 8px)',
      background: '#202c33',
      borderTop: '1px solid #222e35',
      display: 'flex',
      alignItems: 'center',
      gap: 'clamp(6px, 2vw, 8px)',
      flexShrink: 0,
      position: 'sticky',
      bottom: 0,
      zIndex: 3
    }}>
      {sessionOpen ? (
        <>
          {/* Media buttons */}
          <button
            title="Add image"
            onClick={openModal}
            style={{
              background: '#2a3942',
              color: '#e9edef',
              border: '1px solid #3b4a52',
              borderRadius: 8,
              padding: 'clamp(8px, 2.2vw, 10px)',
              cursor: 'pointer',
              touchAction: 'manipulation',
              minHeight: 40,
              minWidth: 44
            }}
          >🖼️</button>
          {/* audio modal i set to false for the moment until fix issue with whastapp  */}
          <button
            title="Record audio"
            onClick={() => setShowAudioModal(false)}
            style={{
              background: '#2a3942',
              color: '#e9edef',
              border: '1px solid #3b4a52',
              borderRadius: 8,
              padding: 'clamp(8px, 2.2vw, 10px)',
              cursor: 'pointer',
              touchAction: 'manipulation',
              minHeight: 40,
              minWidth: 44
            }}
          >🎤</button>

          <input
            style={{
              flexGrow: 1,
              background: '#2a3942',
              border: '1px solid #3b4a52',
              color: '#e9edef',
              borderRadius: 8,
              padding: 'clamp(10px, 2.6vw, 12px)',
              outline: 'none',
              fontSize: 'clamp(14px, 3.2vw, 16px)',
              minHeight: 'clamp(36px, 6vh, 44px)'
            }}
            value={draft}
            onChange={e => setDraft(e.target.value)}
            placeholder="Type a message…"
          />
          <button
            onClick={send}
            style={{
              background: '#00a884',
              color: '#0c1317',
              border: 'none',
              borderRadius: 8,
              padding: 'clamp(10px, 2.6vw, 12px) clamp(12px, 3vw, 14px)',
              fontWeight: 700,
              cursor: 'pointer',
              touchAction: 'manipulation',
              minHeight: 40,
              minWidth: 56
            }}
          >
            Send
          </button>
        </>
      ) : userId === 2 ? (
        <button
          onClick={sendTemplate}
          style={{
            width: '100%',
            background: '#00a884',
            color: '#0c1317',
            border: 'none',
            borderRadius: 8,
            padding: 'clamp(12px, 3vw, 14px)',
            fontWeight: 700,
            cursor: 'pointer',
            touchAction: 'manipulation',
            minHeight: 44
          }}
        >
          Enviar plantilla de “Saludo”
        </button>
      ): (
        <button
          disabled
          aria-disabled="true"
          title="Chat cerrado: solo el admin puede enviar plantillas"
          style={{
            width: '100%',
            background: '#2a3942',
            color: '#94a3b8',
            border: '1px solid #3b4a52',
            borderRadius: 8,
            padding: 'clamp(12px, 3vw, 14px)',
            fontWeight: 700,
            cursor: 'not-allowed',
            minHeight: 44
          }}
        >
          Chat cerrado
        </button>
      )}
    </div>

    {/* Image Modal */}
    {showModal && (
      <div
        style={{
          position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999,
          padding: 'clamp(8px, 2vw, 12px)'
        }}
        onClick={(e) => { if (e.target === e.currentTarget) closeModal(); }}
      >
        <div style={{
          width: 420, maxWidth: '92vw', maxHeight: '90dvh', overflowY: 'auto',
          background: '#111b21', color: '#e9edef',
          borderRadius: 10, padding: 16, boxShadow: '0 12px 32px rgba(0,0,0,0.4)'
        }}>
          <h3 style={{ margin: 0, marginBottom: 12, fontSize: 'clamp(16px, 3.4vw, 18px)' }}>Send an image</h3>

          <div
            onDrop={onDrop}
            onDragOver={onDragOver}
            style={{
              border: '2px dashed #2a3942',
              borderRadius: 10,
              padding: 18,
              textAlign: 'center',
              marginBottom: 12,
              background: '#0b141a'
            }}
          >
            {previewURL ? (
              <img
                src={previewURL}
                alt=""
                style={{ maxWidth: '100%', maxHeight: 260, objectFit: 'contain', borderRadius: 8 }}
              />
            ) : (
              <div style={{ color: '#8696a0' }}>
                Drag & drop an image here<br/>or
              </div>
            )}
            <div style={{ marginTop: 10 }}>
              <label style={{ cursor: 'pointer', color: '#9de1fe' }}>
                Choose file
                <input
                  type="file"
                  accept="image/*"
                  onChange={pickFile}
                  style={{ display: 'none' }}
                />
              </label>
            </div>
          </div>

          <input
            type="text"
            placeholder="Caption (optional)"
            value={caption}
            onChange={e => setCaption(e.target.value)}
            style={{
              width: '100%', padding: 10, marginBottom: 12,
              background: '#2a3942', color: '#e9edef',
              border: '1px solid #3b4a52', borderRadius: 8, outline: 'none',
              fontSize: 'clamp(14px, 3.2vw, 16px)'
            }}
          />

          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
            <button
              onClick={closeModal}
              disabled={uploading}
              style={{
                background: '#2a3942', color: '#e9edef',
                border: '1px solid #3b4a52', borderRadius: 8, padding: '8px 12px',
                cursor: 'pointer', touchAction: 'manipulation', minHeight: 40
              }}
            >
              Cancel
            </button>
            <button
              onClick={sendImage}
              disabled={!file || uploading}
              style={{
                background: '#00a884', color: '#0c1317',
                border: 'none', borderRadius: 8, padding: '8px 12px',
                fontWeight: 700,
                cursor: file && !uploading ? 'pointer' : 'default',
                opacity: file && !uploading ? 1 : 0.7,
                touchAction: 'manipulation',
                minHeight: 40
              }}
            >
              {uploading ? 'Sending…' : 'Send image'}
            </button>
          </div>
        </div>
      </div>
    )}

    {/* Audio Modal */}
    {showAudioModal && (
      <div
        style={{
          position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999,
          padding: 'clamp(8px, 2vw, 12px)'
        }}
        onClick={(e) => { if (e.target === e.currentTarget) setShowAudioModal(false); }}
      >
        <div style={{
          width: 420, maxWidth: '92vw', maxHeight: '90dvh', overflowY: 'auto',
          background: '#111b21', color: '#e9edef',
          borderRadius: 10, padding: 16, boxShadow: '0 12px 32px rgba(0,0,0,0.4)'
        }}>
          <h3 style={{ margin: 0, marginBottom: 12, fontSize: 'clamp(16px, 3.4vw, 18px)' }}>Record & send audio</h3>

          <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
            <button
              disabled={recording}
              onClick={startRecording}
              style={{
                background: recording ? '#2a3942' : '#00a884',
                color: recording ? '#8696a0' : '#0c1317',
                border: 'none', borderRadius: 8, padding: '8px 12px',
                fontWeight: 700, cursor: recording ? 'default' : 'pointer',
                touchAction: 'manipulation', minHeight: 40
              }}
            >
              Start
            </button>
            <button
              disabled={!recording}
              onClick={stopRecording}
              style={{
                background: !recording ? '#2a3942' : '#ef476f',
                color: '#e9edef',
                border: 'none', borderRadius: 8, padding: '8px 12px',
                fontWeight: 700, cursor: recording ? 'pointer' : 'default',
                touchAction: 'manipulation', minHeight: 40
              }}
            >
              Stop
            </button>
          </div>

          {audioURL ? (
            <audio controls src={audioURL} style={{ width: '100%', marginBottom: 12 }} />
          ) : (
            <div style={{ color: '#8696a0', marginBottom: 12 }}>No recording yet.</div>
          )}

          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
            <button
              onClick={() => { setShowAudioModal(false); setAudioBlob(null); setAudioURL(''); }}
              disabled={uploading}
              style={{
                background: '#2a3942', color: '#e9edef',
                border: '1px solid #3b4a52', borderRadius: 8, padding: '8px 12px',
                cursor: 'pointer', touchAction: 'manipulation', minHeight: 40
              }}
            >
              Cancel
            </button>
            <button
              onClick={async () => {
                if (!audioBlob) return;
                setUploading(true);
                try {
                  const fd = new FormData();
                  fd.append('audio', audioBlob, 'recording.ogg');
                  const resp = await fetch(`${process.env.REACT_APP_API_URL}/api/admin/chats/${chatId}/media`, {
                    method: 'POST',
                    body: fd
                  });
                  if (!resp.ok) console.error('audio send failed', await resp.text());
                  setShowAudioModal(false);
                  setAudioBlob(null);
                  setAudioURL('');
                } catch (e) {
                  console.error(e);
                } finally {
                  setUploading(false);
                }
              }}
              disabled={!audioBlob || uploading}
              style={{
                background: '#00a884', color: '#0c1317',
                border: 'none', borderRadius: 8, padding: '8px 12px',
                fontWeight: 700,
                cursor: audioBlob && !uploading ? 'pointer' : 'default',
                opacity: audioBlob && !uploading ? 1 : 0.7,
                touchAction: 'manipulation',
                minHeight: 40
              }}
            >
              {uploading ? 'Sending…' : 'Send audio'}
            </button>
          </div>
        </div>
      </div>
    )}
  </div>
);
}